// Leergut zurück: vom Kunden (Belegart 'empties_intake' – im Markt oder beim Kunden durch den Fahrer) und an den
// Lieferanten (Belegart 'empties_return' – das Leergut geht mit seinem Fahrzeug mit). Beide Belege buchen über den
// Belegkern: Leergutkonto des Partners, Leergutbestand am Standort und den Geldausgleich – genau einmal.
import { PERMISSION_COSTS_READ, defineContract } from '@shared/contract'
import { defineDocumentContracts, zDocumentView } from '@shared/document'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { zDay, zOptionalText, zReturnEntries } from './common'

export const CUSTOMER_RETURN_TYPE = 'empties_intake'
export const SUPPLIER_RETURN_TYPE = 'empties_return'

/**
 * Ausgleich einer Leergutrücknahme: credit = Gutschrift auf das Kundenkonto (offener Posten zugunsten des Kunden,
 * wird mit der nächsten Rechnung verrechnet) · payout = Auszahlung in bar – nur an Standorten ohne Kasse; mit Kasse
 * zahlt die Kasse aus (Bon nur mit Leergut, abgesichert über die Signatureinheit).
 */
export const SETTLEMENTS = ['credit', 'payout'] as const
export type Settlement = (typeof SETTLEMENTS)[number]

export const customerReturn = defineDocumentContracts({
  module: 'deposits',
  entity: 'customerReturn',
  documentType: CUSTOMER_RETURN_TYPE,
  title: 'Leergutrücknahme',
  titlePlural: 'Leergutrücknahmen',
  permissions: { read: 'deposits.customerReturn.read', write: 'deposits.customerReturn.create', post: 'deposits.customerReturn.create', reverse: 'deposits.account.adjust' },
  keywords: ['Leergut', 'Leergutrücknahme', 'Pfand zurück', 'leere Kästen', 'Pfandrückgabe', 'Pfand auszahlen'],
})

export const supplierReturn = defineDocumentContracts({
  module: 'deposits',
  entity: 'supplierReturn',
  documentType: SUPPLIER_RETURN_TYPE,
  title: 'Leergutrückgabe',
  titlePlural: 'Leergutrückgaben',
  permissions: { read: 'deposits.supplierReturn.read', write: 'deposits.supplierReturn.create', post: 'deposits.supplierReturn.create', reverse: 'deposits.account.adjust' },
  keywords: ['Leergutrückgabe', 'Leergut an Lieferant', 'Leergut Brauerei', 'Leergut abholen', 'Leergutabholung'],
})

export const customerReturnGet = customerReturn.get
export const customerReturnList = customerReturn.list
export const customerReturnSaveDraft = customerReturn.saveDraft
export const customerReturnPost = customerReturn.post
export const customerReturnReverse = customerReturn.reverse
export const customerReturnTransition = customerReturn.transition
export const customerReturnPrint = customerReturn.print
export const customerReturnDiscard = customerReturn.discard
export const supplierReturnGet = supplierReturn.get
export const supplierReturnList = supplierReturn.list
export const supplierReturnSaveDraft = supplierReturn.saveDraft
export const supplierReturnPost = supplierReturn.post
export const supplierReturnReverse = supplierReturn.reverse
export const supplierReturnTransition = supplierReturn.transition
export const supplierReturnPrint = supplierReturn.print
export const supplierReturnDiscard = supplierReturn.discard

const zReturnSummary = z.object({
  /** was zurückkam, in einfachen Pfandarten */
  leaves: z.array(z.object({ depositTypeId: zId, name: z.string(), qty: z.int(), amountCents: z.int() })),
})

export const customerReturnBook = defineContract({
  name: 'deposits.customerReturn.book',
  title: 'Leergut zurücknehmen',
  description: 'Nimmt gezähltes Leergut von einem Kunden oder der Laufkundschaft zurück und bucht es in einem Schritt.',
  llm: {
    description:
      'Bucht eine Leergutrücknahme an einem Standort (Markt oder Fahrzeug) in einem Schritt: je Zeile Pfandart und ' +
      'Stück in Tausendstel (3 Kästen = 3000); bei einem Mischkasten oder nicht vollen Kasten `contents` mit dem ' +
      'tatsächlichen Inhalt in einfachen Pfandarten. Mit `partnerId` sinkt das Leergutkonto des Kunden; ohne Kunde ' +
      '(Laufkundschaft) gibt es nur Bestand und Auszahlung. `settlement`: credit = Gutschrift auf das Kundenkonto ' +
      '(nur mit Kunde), payout = Auszahlung in bar – nur an Standorten ohne Kasse; an einem Standort mit Kasse zahlt ' +
      'die Kasse bares Pfand aus (pos.sale.edit mit setEmpties, Bon nur mit Leergut), hier geht dort nur die ' +
      'Gutschrift. Der Leergutbestand am Standort steigt. Für Rückgaben an ' +
      'Lieferanten deposits.supplierReturn.book, für Kontokorrekturen ohne Ware deposits.account.adjust.',
    keywords: ['Leergut zurücknehmen', 'Leergutrücknahme', 'Pfand auszahlen', 'leere Kästen zurück', 'Leergut gutschreiben', 'Pfandrückgabe'],
  },
  input: z.strictObject({
    locationId: zId,
    partnerId: zId.nullish(),
    /** ohne Angabe: mit Kunde Gutschrift, ohne Kunde Auszahlung */
    settlement: z.enum(SETTLEMENTS).optional(),
    documentDate: zDay.optional(),
    note: zOptionalText(500),
    entries: zReturnEntries,
  }),
  output: zReturnSummary.extend({
    document: zDocumentView,
    settlement: z.enum(SETTLEMENTS),
    /** Betrag der Gutschrift bzw. Auszahlung in Cent (brutto) */
    refundCents: z.int(),
    message: z.string(),
  }),
  permission: 'deposits.customerReturn.create',
  risk: 'financial',
  scope: (input) => [input.locationId],
  fieldPermissions: { 'document.lines.*.costCents': PERMISSION_COSTS_READ },
  effects: { reversible: true },
  limits: { maxItems: 100, itemCount: (input) => input.entries.length },
})

/** Vorab rechnen, was eine Rücknahme wert ist – für den Zähler am Telefon (ändert nichts). */
export const returnQuote = defineContract({
  name: 'deposits.customerReturn.quote',
  title: 'Leergut bewerten',
  description: 'Rechnet den Pfandwert gezählten Leerguts aus, ohne etwas zu buchen.',
  llm: {
    description:
      'Rechnet für gezähltes Leergut (Pfandarten mit Stück, Mischkästen mit `contents`) den Pfandwert zum Stichtag ' +
      'aus und zerlegt es in einfache Pfandarten – ohne zu buchen. Verwenden für „was bekommt die Kundin für 2 Kästen ' +
      'und 7 Flaschen“.',
    keywords: ['Leergut bewerten', 'Pfandwert', 'was ist das Leergut wert'],
  },
  input: z.strictObject({ entries: zReturnEntries, date: zDay.optional() }),
  output: zReturnSummary.extend({ amountCents: z.int() }),
  permission: 'deposits.type.read',
  risk: 'read',
})

export const supplierReturnBook = defineContract({
  name: 'deposits.supplierReturn.book',
  title: 'Leergut an Lieferanten zurückgeben',
  description: 'Bucht Leergut, das mit dem Fahrzeug des Lieferanten zurückgeht, in einem Schritt.',
  llm: {
    description:
      'Bucht eine Leergutrückgabe an einen Lieferanten in einem Schritt: Standort (meist das Zentrallager), ' +
      'Lieferant und je Zeile Pfandart mit Stück in Tausendstel (200 Kästen = 200000). Der Leergutbestand am Standort ' +
      'und das Leergutkonto des Lieferanten sinken; der Pfandwert steht als Guthaben gegen den Lieferanten in den ' +
      'offenen Posten. Scheitert, wenn weniger Leergut am Standort liegt. Für Leergut vom Kunden ' +
      'deposits.customerReturn.book.',
    keywords: ['Leergutrückgabe', 'Leergut an Brauerei', 'Leergut mitgeben', 'Leergutabholung', 'leere Kästen abholen lassen'],
  },
  input: z.strictObject({
    locationId: zId,
    supplierId: zId,
    documentDate: zDay.optional(),
    /** Lieferschein- oder Abholnummer des Lieferanten */
    externalRef: zOptionalText(120),
    note: zOptionalText(500),
    entries: zReturnEntries,
  }),
  output: zReturnSummary.extend({
    document: zDocumentView,
    /** Pfandwert der Rückgabe in Cent (brutto) – Guthaben gegen den Lieferanten */
    creditCents: z.int(),
    message: z.string(),
  }),
  permission: 'deposits.supplierReturn.create',
  risk: 'financial',
  scope: (input) => [input.locationId],
  fieldPermissions: { 'document.lines.*.costCents': PERMISSION_COSTS_READ },
  effects: { reversible: true },
  limits: { maxItems: 100, itemCount: (input) => input.entries.length },
})
